import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEND_MESSAGE_V2 } from '../../../graphql/operations';
import './widgets.css';

const DEFAULT_ORDER_FIELDS = [
  { name: 'type', label: 'Type', required: true, type: 'search-select', options: [] },
  { name: 'status', label: 'Status', required: true, type: 'search-select', options: [] },
  { name: 'orderingOrganization', label: 'Ordering organization', required: true, type: 'search-select', options: [] },
  { name: 'physician', label: 'Physician', required: false, type: 'search-select', options: [] },
  { name: 'details', label: 'Details', required: false, type: 'textarea' }
];

function normalize(value) {
  return String(value || '').trim();
}

function toBase64Utf8(value) {
  if (typeof TextEncoder !== 'undefined') {
    const encoded = new TextEncoder().encode(value);
    let binary = '';
    encoded.forEach((charCode) => {
      binary += String.fromCharCode(charCode);
    });
    return window.btoa(binary);
  }
  const encoded = encodeURIComponent(value).replace(
    /%([0-9A-F]{2})/g,
    (_match, hexByte) => String.fromCharCode(parseInt(hexByte, 16))
  );
  return window.btoa(encoded);
}

function normalizeOptions(options) {
  const normalized = [];
  const seenValues = new Set();
  (options || []).forEach((option) => {
    if (option && typeof option === 'object') {
      const label = normalize(option.label);
      const value = normalize(option.value);
      if (!label || !value) return;
      const key = value.toLowerCase();
      if (seenValues.has(key)) return;
      seenValues.add(key);
      normalized.push({ label, value });
      return;
    }
    const raw = normalize(option);
    if (!raw) return;
    const key = raw.toLowerCase();
    if (seenValues.has(key)) return;
    seenValues.add(key);
    normalized.push({ label: raw, value: raw });
  });
  return normalized.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function filterOptions(options, inputValue) {
  const query = normalize(inputValue).toLowerCase();
  if (!query) return options;
  return options.filter((option) => (
    normalize(option.label).toLowerCase().includes(query)
    || normalize(option.value).toLowerCase().includes(query)
  ));
}

function findMatchingOption(rawValue, options) {
  const value = normalize(rawValue).toLowerCase();
  if (!value) return null;
  return options.find(
    (option) =>
      normalize(option.value).toLowerCase() === value
      || normalize(option.label).toLowerCase() === value
  ) || null;
}

function statusOptionsForType(statusOptionsByType, selectedType) {
  const normalizedType = normalize(selectedType).toLowerCase();
  if (!normalizedType) return [];
  const entries = Object.entries(statusOptionsByType || {});
  const match = entries.find(([key]) => normalize(key).toLowerCase() === normalizedType);
  return match ? normalizeOptions(match[1]) : [];
}

function buildDisplayValues(fields, values, statusOptionsByType) {
  const next = {};
  fields.forEach((field) => {
    const name = field.name;
    const raw = normalize(values[name]);
    if (!raw) {
      next[name] = '';
      return;
    }
    const options = field.name === 'status'
      ? statusOptionsForType(statusOptionsByType, values.type)
      : normalizeOptions(field.options);
    const match = findMatchingOption(raw, options);
    const shouldHideUidFallback = (
      name === 'physician'
      && !match
      && /^[0-9a-f]{8}$/i.test(raw)
    );
    next[name] = shouldHideUidFallback ? '' : (match ? match.label : raw);
  });
  return next;
}

function computeDelta(initialValues, currentValues) {
  const delta = {};
  Object.keys(currentValues || {}).forEach((key) => {
    const before = normalize(initialValues?.[key]);
    const after = normalize(currentValues?.[key]);
    if (before !== after) {
      delta[key] = after;
    }
  });
  return delta;
}

function validateForm({ fields, values, mode, statusOptionsByType }) {
  const errors = {};

  fields.forEach((field) => {
    const name = field.name;
    const value = normalize(values[name]);
    const isRequired = Boolean(field.required);
    const isSearchSelect = field.type === 'search-select';

    if (isRequired && !value) {
      errors[name] = 'Required';
      return;
    }

    if (isSearchSelect && value) {
      const options = name === 'status'
        ? statusOptionsForType(statusOptionsByType, values.type)
        : normalizeOptions(field.options);
      const match = findMatchingOption(value, options);
      if (!match) {
        errors[name] = 'Select a valid option';
      }
    }
  });

  if (String(mode || '').toLowerCase() === 'update') {
    const delta = computeDelta(values.__initialValues || {}, values);
    if (Object.keys(delta).length === 0) {
      errors.details = 'Update at least one field before submitting.';
    }
  }

  return errors;
}

function OrderFormCard({ data, conversationId }) {
  const [sendMessage] = useMutation(SEND_MESSAGE_V2);
  const formFromCard = data.form || {};
  const payloadFromConfirm = data.confirm?.action?.payload || {};
  const parsedFields = Array.isArray(formFromCard.fields)
    ? formFromCard.fields
    : (Array.isArray(payloadFromConfirm.fields) ? payloadFromConfirm.fields : []);
  const fields = parsedFields.length > 0 ? parsedFields : DEFAULT_ORDER_FIELDS;
  const mode = String(formFromCard.mode || payloadFromConfirm.mode || 'create').toLowerCase();

  const statusOptionsByType = useMemo(
    () => (
      (formFromCard.statusOptionsByType && typeof formFromCard.statusOptionsByType === 'object')
        ? formFromCard.statusOptionsByType
        : ((payloadFromConfirm.statusOptionsByType && typeof payloadFromConfirm.statusOptionsByType === 'object')
          ? payloadFromConfirm.statusOptionsByType
          : {})
    ),
    [formFromCard.statusOptionsByType, payloadFromConfirm.statusOptionsByType]
  );

  const initialValues = useMemo(
    () => {
      const source = {
        ...(formFromCard.values || {}),
        ...(payloadFromConfirm.values || {})
      };
      const normalized = {};
      fields.forEach((field) => {
        normalized[field.name] = normalize(source[field.name]);
      });
      return normalized;
    },
    [fields, formFromCard.values, payloadFromConfirm.values]
  );
  const [values, setValues] = useState(initialValues);
  const [displayValues, setDisplayValues] = useState(
    buildDisplayValues(fields, initialValues, statusOptionsByType)
  );
  const [errors, setErrors] = useState({ ...(formFromCard.errors || payloadFromConfirm.errors || {}) });
  const [submitState, setSubmitState] = useState('idle');
  const [cancelState, setCancelState] = useState('idle');
  const [openSelectName, setOpenSelectName] = useState('');
  const cardRef = useRef(null);

  const pendingActionId = formFromCard.pendingActionId
    || payloadFromConfirm.pendingActionId
    || data.submit?.action?.payload?.pendingActionId
    || data.cancel?.action?.payload?.pendingActionId
    || '';

  const currentStatusOptions = useMemo(
    () => statusOptionsForType(statusOptionsByType, values.type),
    [statusOptionsByType, values.type]
  );

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setOpenSelectName('');
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  const setFieldValue = (fieldName, nextValue) => {
    setValues((prev) => {
      const updated = {
        ...prev,
        [fieldName]: normalize(nextValue)
      };
      if (fieldName === 'type') {
        const validStatuses = statusOptionsForType(statusOptionsByType, updated.type);
        const currentStatus = normalize(updated.status).toLowerCase();
        const isCurrentStatusValid = validStatuses.some(
          (option) => normalize(option.value).toLowerCase() === currentStatus
        );
        if (!isCurrentStatusValid) {
          updated.status = '';
          setDisplayValues((prevDisplay) => ({ ...prevDisplay, status: '' }));
        }
      }
      return updated;
    });
    setErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleSearchInput = (field, inputValue) => {
    const fieldName = field.name;
    const options = fieldName === 'status'
      ? currentStatusOptions
      : normalizeOptions(field.options);
    setDisplayValues((prev) => ({ ...prev, [fieldName]: inputValue }));
    const match = findMatchingOption(inputValue, options);
    setFieldValue(fieldName, match ? match.value : inputValue);
    setOpenSelectName(fieldName);
  };

  const selectOption = (field, option) => {
    setDisplayValues((prev) => ({ ...prev, [field.name]: option.label }));
    setFieldValue(field.name, option.value);
    setOpenSelectName('');
  };

  const handleBasicInput = (fieldName, inputValue) => {
    setDisplayValues((prev) => ({ ...prev, [fieldName]: inputValue }));
    setFieldValue(fieldName, inputValue);
  };

  const onSubmit = async () => {
    if (!conversationId || !pendingActionId || submitState === 'loading' || submitState === 'sent') return;

    const validationErrors = validateForm({
      fields,
      values: { ...values, __initialValues: initialValues },
      mode,
      statusOptionsByType
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSubmitState('error');
      return;
    }

    const delta = computeDelta(initialValues, values);
    const payloadB64 = toBase64Utf8(JSON.stringify(delta));
    const command = mode === 'update'
      ? `/submit-order-update ${pendingActionId} ${payloadB64}`
      : `/submit-order-create ${pendingActionId} ${payloadB64}`;

    setSubmitState('loading');
    try {
      await sendMessage({
        variables: {
          conversationId,
          content: command
        }
      });
      setSubmitState('sent');
      setErrors({});
    } catch (error) {
      console.error('Failed to submit order form:', error);
      setSubmitState('error');
    }
  };

  const onCancel = async () => {
    if (!conversationId || !pendingActionId || cancelState === 'loading' || cancelState === 'sent') return;
    setCancelState('loading');
    try {
      await sendMessage({
        variables: {
          conversationId,
          content: `/discard ${pendingActionId}`
        }
      });
      setCancelState('sent');
    } catch (error) {
      console.error('Failed to cancel order form:', error);
      setCancelState('idle');
    }
  };

  return (
    <div className="widget-card widget-card-form" ref={cardRef}>
      <div className="widget-card-status-header widget-header-warning">
        <span className="widget-status-dot" />
        <span className="widget-status-text">{data.status?.text || 'Form required'}</span>
      </div>

      <div className="widget-card-content">
        {(data.children || []).map((child, index) => {
          if (child.type === 'Title') {
            return <h4 key={index} className="widget-card-title">{child.value}</h4>;
          }
          if (child.type === 'Text') {
            return <p key={index} className="widget-text">{child.value}</p>;
          }
          if (child.type === 'Caption') {
            return <p key={index} className="widget-caption">{child.value}</p>;
          }
          if (child.type === 'Divider') {
            return <hr key={index} className="widget-divider" />;
          }
          return null;
        })}

        <div className="widget-form-grid">
          {fields.map((field) => {
            const fieldName = field.name;
            const value = String(values[fieldName] || '');
            const shownValue = String(displayValues[fieldName] ?? value);
            const hasError = Boolean(errors[fieldName]);
            const label = `${field.label}${field.required ? ' *' : ''}`;
            const options = fieldName === 'status'
              ? currentStatusOptions
              : normalizeOptions(field.options);
            const filteredOptions = filterOptions(options, shownValue);

            if (field.type === 'textarea') {
              return (
                <label key={fieldName} className="widget-form-field widget-form-field-full">
                  <span className="widget-form-label">{label}</span>
                  <textarea
                    value={shownValue}
                    onChange={(event) => handleBasicInput(fieldName, event.target.value)}
                    className={`widget-form-input widget-form-textarea ${hasError ? 'has-error' : ''}`}
                    rows={3}
                  />
                  {hasError && <span className="widget-form-error">{errors[fieldName]}</span>}
                </label>
              );
            }

            if (field.type === 'search-select') {
              return (
                <label key={fieldName} className="widget-form-field">
                  <span className="widget-form-label">{label}</span>
                  <div className="widget-search-select">
                    <div className="widget-search-select-input-wrap">
                      <input
                        type="text"
                        value={shownValue}
                        onFocus={() => setOpenSelectName(fieldName)}
                        onChange={(event) => handleSearchInput(field, event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            setOpenSelectName('');
                          }
                          if (event.key === 'ArrowDown') {
                            setOpenSelectName(fieldName);
                          }
                        }}
                        className={`widget-form-input widget-search-select-input ${hasError ? 'has-error' : ''}`}
                      />
                      <span className="widget-search-select-caret" aria-hidden="true">v</span>
                    </div>
                    {openSelectName === fieldName && filteredOptions.length > 0 && (
                      <div className="widget-search-select-menu" role="listbox" aria-label={`${field.label} options`}>
                        {filteredOptions.map((option) => (
                          <button
                            key={`${fieldName}-${option.value}`}
                            type="button"
                            className="widget-search-select-option"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectOption(field, option)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {hasError && <span className="widget-form-error">{errors[fieldName]}</span>}
                </label>
              );
            }

            return (
              <label key={fieldName} className="widget-form-field">
                <span className="widget-form-label">{label}</span>
                <input
                  type={field.type || 'text'}
                  value={shownValue}
                  onChange={(event) => handleBasicInput(fieldName, event.target.value)}
                  className={`widget-form-input ${hasError ? 'has-error' : ''}`}
                />
                {hasError && <span className="widget-form-error">{errors[fieldName]}</span>}
              </label>
            );
          })}
        </div>

        <div className="widget-card-actions">
          <button
            type="button"
            className={`widget-btn widget-btn-confirm ${submitState === 'loading' ? 'is-loading' : ''} ${submitState === 'sent' ? 'is-done' : ''}`}
            onClick={onSubmit}
            disabled={!conversationId || !pendingActionId || submitState === 'loading' || submitState === 'sent'}
          >
            {submitState === 'loading' && <span className="widget-btn-spinner" aria-hidden="true" />}
            {submitState === 'loading' ? 'Submitting...' : submitState === 'sent' ? 'Submitted' : (data.submit?.label || 'Submit details')}
          </button>
          <button
            type="button"
            className={`widget-btn widget-btn-cancel ${cancelState === 'loading' ? 'is-loading' : ''} ${cancelState === 'sent' ? 'is-done' : ''}`}
            onClick={onCancel}
            disabled={!conversationId || !pendingActionId || cancelState === 'loading' || cancelState === 'sent'}
          >
            {cancelState === 'loading' ? 'Canceling...' : cancelState === 'sent' ? 'Canceled' : (data.cancel?.label || 'Cancel')}
          </button>
        </div>

        {submitState === 'error' && (
          <p className="widget-caption">Please correct the highlighted fields and try again.</p>
        )}
        {submitState === 'sent' && (
          <p className="widget-caption widget-responded">Submitted. I’m validating the order details.</p>
        )}
      </div>
    </div>
  );
}

export default OrderFormCard;
